/**
 * Shared Emoji Reaction Button Component
 * Can be used across different pages for liking/reacting to content
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Avatar,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import {
  AddReaction as AddReactionIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

// Available emoji reactions
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👏'];

/**
 * Emoji Reaction Button Component
 * @param {string} contentType - The type of content (e.g., 'call', 'reflection', 'comment')
 * @param {string} objectId - The UUID of the object being reacted to
 * @param {object} currentUser - The current logged-in user
 * @param {string} size - Size of the button ('small' or 'medium')
 */
export default function EmojiReactionButton({ contentType, objectId, currentUser, size = 'small' }) {
  const [reactions, setReactions] = useState({});  // { emoji: count }
  const [reactionUsers, setReactionUsers] = useState({});  // { emoji: [user_info, ...] }
  const [myReaction, setMyReaction] = useState(null);  // User's current reaction emoji
  const [myLikeId, setMyLikeId] = useState(null);  // ID of user's like for deletion
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  // Reactions load when the button comes near the screen, not for every post of a long feed at once
  const [nearScreen, setNearScreen] = useState(false);
  useEffect(() => {
    const el = pickerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setNearScreen(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setNearScreen(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (nearScreen) fetchReactions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId, nearScreen]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchReactions = async () => {
    try {
      const response = await axiosService.get('/interact/likes/', {
        params: {
          content_type: contentType,
          object_id: objectId,
        }
      });
      const likes = response.data.results || response.data || [];
      
      // Count reactions by emoji and collect user info
      const reactionCounts = {};
      const reactionUsersMap = {};
      let userReaction = null;
      let userLikeId = null;
      
      likes.forEach(like => {
        const emoji = like.reaction_type || '👍';
        reactionCounts[emoji] = (reactionCounts[emoji] || 0) + 1;
        if (!reactionUsersMap[emoji]) reactionUsersMap[emoji] = [];
        if (like.user_info) reactionUsersMap[emoji].push(like.user_info);
        
        if (String(like.user) === String(currentUser?.id)) {
          userReaction = emoji;
          userLikeId = like.id;
        }
      });
      
      setReactions(reactionCounts);
      setReactionUsers(reactionUsersMap);
      setMyReaction(userReaction);
      setMyLikeId(userLikeId);
    } catch (err) {
      console.error('Failed to fetch reactions:', err);
    }
  };

  const handleReaction = async (emoji) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setShowPicker(false);
      
      // If clicking the same emoji, remove the reaction
      if (myReaction === emoji && myLikeId) {
        await axiosService.delete(`/interact/likes/${myLikeId}/`);
        setReactions(prev => {
          const updated = { ...prev };
          updated[emoji] = Math.max(0, (updated[emoji] || 1) - 1);
          if (updated[emoji] === 0) delete updated[emoji];
          return updated;
        });
        setReactionUsers(prev => {
          const updated = { ...prev };
          updated[emoji] = (updated[emoji] || []).filter(u => String(u.id) !== String(currentUser?.id));
          return updated;
        });
        setMyReaction(null);
        setMyLikeId(null);
      } else {
        // If user has existing reaction, remove it first
        if (myReaction && myLikeId) {
          await axiosService.delete(`/interact/likes/${myLikeId}/`);
          setReactions(prev => {
            const updated = { ...prev };
            updated[myReaction] = Math.max(0, (updated[myReaction] || 1) - 1);
            if (updated[myReaction] === 0) delete updated[myReaction];
            return updated;
          });
          setReactionUsers(prev => {
            const updated = { ...prev };
            updated[myReaction] = (updated[myReaction] || []).filter(u => String(u.id) !== String(currentUser?.id));
            return updated;
          });
        }
        
        // Create new reaction
        const response = await axiosService.post('/interact/likes/', {
          content_type: contentType,
          object_id: objectId,
          reaction_type: emoji,
        });
        
        setReactions(prev => ({
          ...prev,
          [emoji]: (prev[emoji] || 0) + 1
        }));
        if (currentUser) {
          setReactionUsers(prev => ({
            ...prev,
            [emoji]: [...(prev[emoji] || []), { id: currentUser.id, name: currentUser.name || currentUser.full_name, avatar: currentUser.avatar }]
          }));
        }
        setMyReaction(emoji);
        setMyLikeId(response.data.id);
      }
    } catch (err) {
      console.error('Failed to toggle reaction:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }} ref={pickerRef}>
      {/* Display existing reactions with tooltip */}
      {Object.entries(reactions).map(([emoji, count]) => {
        const users = reactionUsers[emoji] || [];
        const shown = users.slice(0, 5);
        const remaining = count - shown.length;
        const tooltipContent = (
          <Box sx={{ p: 0.5, minWidth: 100 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', display: 'block', mb: 0.5 }}>
              {emoji} {count} {count === 1 ? 'reaction' : 'reactions'}
            </Typography>
            {shown.length > 0 && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 0.5 }} />
                <Stack spacing={0.25}>
                  {shown.map((u, i) => (
                    <Box key={i} display="flex" alignItems="center" gap={0.75}>
                      <Avatar src={u.avatar} alt={u.name} sx={{ width: 16, height: 16, fontSize: 10 }}>
                        {u.name?.charAt(0)}
                      </Avatar>
                      <Typography variant="caption" sx={{ color: '#fff', lineHeight: 1.3 }}>
                        {u.name || 'Unknown'}
                      </Typography>
                    </Box>
                  ))}
                  {remaining > 0 && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
                      and {remaining} more
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Box>
        );
        return (
          <Tooltip
            key={emoji}
            title={tooltipContent}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'rgba(30,30,30,0.95)',
                  borderRadius: 1.5,
                  p: 1,
                  boxShadow: 3,
                  '& .MuiTooltip-arrow': { color: 'rgba(30,30,30,0.95)' },
                },
              },
            }}
          >
            <Chip
              label={`${emoji} ${count}`}
              size="small"
              onClick={() => handleReaction(emoji)}
              disabled={loading || !currentUser}
              variant={myReaction === emoji ? 'filled' : 'outlined'}
              color={myReaction === emoji ? 'primary' : 'default'}
              sx={{
                cursor: 'pointer',
                fontSize: '0.9rem',
                height: 18,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Tooltip>
        );
      })}
      
      {/* Add reaction button */}
      <IconButton
        size={size}
        onClick={() => setShowPicker(!showPicker)}
        disabled={loading || !currentUser}
        sx={{ p: 0.5 }}
      >
        <AddReactionIcon fontSize={size} />
      </IconButton>
      
      {/* Emoji picker popup */}
      {showPicker && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            mb: 0.5,
            p: 0.5,
            display: 'flex',
            gap: 0.5,
            zIndex: 1000,
            borderRadius: 2,
          }}
        >
          {REACTION_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => handleReaction(emoji)}
              sx={{
                fontSize: '1.2rem',
                p: 0.5,
                backgroundColor: myReaction === emoji ? 'primary.light' : 'transparent',
                '&:hover': { transform: 'scale(1.2)' },
                transition: 'transform 0.1s',
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Paper>
      )}
    </Box>
  );
}
